/**
 * The offering-document vault: a product's PPM, term sheet and supplements.
 *
 * This is not ordinary product configuration. An unacknowledged PUBLISHED
 * offering document makes contract-case creation answer 403 — so until a
 * document is uploaded and published here, nobody can sign a contract for that
 * product, and the client-side read and acknowledgement (already built) have
 * nothing to show. Before this screen the whole vault could only be loaded by
 * someone with database and bucket access.
 *
 * Uploading is three steps because the file never passes through our API: the
 * browser PUTs straight to storage with a presigned URL, then the server
 * re-reads what it received and checks its size, its type and its SHA-256
 * before registering the row. Publishing is a separate, reasoned act.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import offeringDocumentsService, {
  DOC_TYPES,
  DOC_TYPE_LABEL,
  type DocType,
  type OfferingDocument,
} from '../../services/offeringDocuments.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const inputStyle = { border: `1px solid ${colors.border}` };

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OfferingDocuments(): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [productId, setProductId] = useState('');
  const [documents, setDocuments] = useState<OfferingDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    portfolioService
      .listAllProducts()
      .then(setProducts)
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load the products.')));
  }, []);

  const reload = useCallback(async () => {
    if (!productId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setDocuments(await offeringDocumentsService.list(productId));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load this product’s documents.'));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { void reload(); }, [reload]);

  const published = documents.filter((d) => d.is_published);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Offering documents"
        subtitle="The PPM and term sheet a client must acknowledge before they can sign a contract for a product."
      />

      <section className="rounded-2xl p-5 mb-6"
               style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <label htmlFor="od-product" className="block text-xs font-semibold mb-1"
               style={{ color: colors.inkSoft }}>
          Product
        </label>
        <select
          id="od-product"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={inputStyle}
        >
          <option value="">— select product —</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </section>

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      {productId && !loading && published.length === 0 && (
        <div className="mb-6 p-3 rounded-lg text-sm"
             style={{ background: colors.warningBg, border: '1px solid #FCD34D', color: colors.ink }}>
          Nothing is published for this product yet, so a client cannot start a contract for it.
        </div>
      )}

      {productId && (
        <UploadPanel
          productId={productId}
          onDone={(msg) => { setFlash(msg); void reload(); }}
          onError={setError}
        />
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {productId && !loading && documents.length > 0 && (
        <section className="rounded-2xl p-5"
                 style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold mb-3" style={{ color: colors.ink }}>In the vault</h2>
          <ul className="space-y-3">
            {documents.map((d) => (
              <DocumentRow
                key={d.document_id}
                document={d}
                onDone={(msg) => { setFlash(msg); void reload(); }}
                onError={setError}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function UploadPanel({ productId, onDone, onError }: Readonly<{
  productId: string;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [docType, setDocType] = useState<DocType>('ppm');
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<string | null>(null);

  const upload = async (): Promise<void> => {
    if (!file) return;
    try {
      setStep('Asking storage for a one-time upload link…');
      const presigned = await offeringDocumentsService.presignUpload(productId, docType);
      setStep('Uploading the file…');
      await offeringDocumentsService.uploadToStorage(presigned.upload_url, file);
      setStep('Checking what arrived…');
      const registered = await offeringDocumentsService.confirmUpload(
        productId, docType, presigned.object_key,
      );
      setFile(null);
      onDone(
        `Version ${registered.version} of the ${DOC_TYPE_LABEL[docType].toLowerCase()} is in the vault, `
        + 'not published yet.',
      );
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'The upload did not complete.'));
    } finally {
      setStep(null);
    }
  };

  const wrongType = file !== null && file.type !== 'application/pdf';

  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Add a version</h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        A new file is always a new version. Nothing is replaced, and nothing becomes binding
        until you publish it.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="od-type" className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Document
          </label>
          <select
            id="od-type"
            value={docType}
            onChange={(e) => setDocType(e.target.value as DocType)}
            disabled={step !== null}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={inputStyle}
          >
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="od-file" className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            PDF
          </label>
          <input
            id="od-file"
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={step !== null}
            className="w-full text-sm"
          />
        </div>
      </div>

      {wrongType && (
        <p className="text-xs mt-2" style={{ color: colors.danger }}>
          Offering documents are PDFs. The server checks the file it receives, so another
          format is refused there too.
        </p>
      )}

      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          disabled={file === null || wrongType || step !== null}
          onClick={() => { void upload(); }}
          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{ background: colors.navy, color: '#FFFFFF' }}
        >
          Upload
        </button>
        {step && <span className="text-sm" style={{ color: colors.muted }}>{step}</span>}
      </div>
    </section>
  );
}

function DocumentRow({ document, onDone, onError }: Readonly<{
  document: OfferingDocument;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = reason.trim().length >= 4 && !busy;

  const publish = async (): Promise<void> => {
    setBusy(true);
    try {
      await offeringDocumentsService.publish(document.document_id, reason.trim());
      setOpen(false);
      setReason('');
      onDone(`${DOC_TYPE_LABEL[document.doc_type]} v${document.version} is published.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not publish the document.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium" style={{ color: colors.ink }}>
            {DOC_TYPE_LABEL[document.doc_type]} · v{document.version}
          </div>
          <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {formatSize(document.file_size)} · added {formatDateTime(document.created_at)}
            {document.published_at && ` · published ${formatDateTime(document.published_at)}`}
          </div>
          <div className="text-xs mt-1 font-mono" style={{ color: colors.mutedSoft }}>
            {document.file_hash.slice(0, 16)}…
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={document.is_published ? 'success' : 'neutral'}>
            {document.is_published ? 'Published' : 'Not published'}
          </StatusBadge>
          {!document.is_published && (
            <button type="button" onClick={() => setOpen((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {open ? 'Cancel' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
            Publishing makes this the version every new contract for this product is signed
            against, and every client must acknowledge it before they can sign.
          </p>
          <label htmlFor={`pub-${document.document_id}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            What changed, or why now?
          </label>
          <input
            id={`pub-${document.document_id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            placeholder="Counsel-approved version of 24 September."
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <button type="button" disabled={!ready} onClick={() => { void publish(); }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            {busy ? 'Publishing…' : 'Publish this version'}
          </button>
        </div>
      )}
    </li>
  );
}
